import { useState } from "react";
import { Quotes, ChartLineUp, Books } from "@phosphor-icons/react";

const TABS = [
  { key: "all", label: "All time" },
  { key: "recent", label: "Since 2021" },
];

const COLOR_MAP = {
  citations: {
    accent: "text-ink",
    bg: "bg-coral",
    soft: "bg-coral/25",
    border: "border-coral",
    rule: "bg-ink/30",
    tilt: "sticker-tilt-l",
  },
  h_index: {
    accent: "text-ink",
    bg: "bg-lime",
    soft: "bg-lime/40",
    border: "border-lime",
    rule: "bg-ink/30",
    tilt: "",
  },
  i10_index: {
    accent: "text-ink",
    bg: "bg-sky",
    soft: "bg-sky/35",
    border: "border-sky",
    rule: "bg-ink/30",
    tilt: "sticker-tilt-r",
  },
};

export const MetricsCards = ({ metrics = {} }) => {
  const [tab, setTab] = useState("all");
  const items = [
    {
      key: "citations",
      label: "Citations",
      value:
        tab === "all" ? metrics.citations_all ?? 0 : metrics.citations_recent ?? 0,
      icon: Quotes,
      blurb: "Total times cited in indexed scholarly literature",
    },
    {
      key: "h_index",
      label: "h-index",
      value: tab === "all" ? metrics.h_index_all ?? 0 : metrics.h_index_recent ?? 0,
      icon: ChartLineUp,
      blurb: "Largest h such that h papers each have ≥ h citations",
    },
    {
      key: "i10_index",
      label: "i10-index",
      value:
        tab === "all" ? metrics.i10_index_all ?? 0 : metrics.i10_index_recent ?? 0,
      icon: Books,
      blurb: "Number of publications with at least 10 citations",
    },
  ];

  return (
    <div data-testid="metrics-cards">
      <div
        role="tablist"
        className="inline-flex border border-ink/15 mb-6 font-mono text-[11px] uppercase tracking-wider no-print"
      >
        {TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
            data-testid={`metrics-tab-${t.key}`}
            className={`px-4 py-2 transition-colors ${
              tab === t.key
                ? "bg-ink text-paper"
                : "bg-transparent text-ink-mute hover:text-ink"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 pt-3 pb-2">
        {items.map((it) => {
          const Icon = it.icon;
          const c = COLOR_MAP[it.key];
          return (
            <div
              key={it.key}
              className={`sticker p-7 lg:p-8 relative ${c.soft} overflow-hidden avoid-break ${c.tilt}`}
              data-testid={`metric-${it.key}`}
            >
              <div className={`absolute top-0 left-0 h-3 w-full ${c.bg}`} />
              <div className="relative flex items-start justify-between mt-2">
                <div className="overline">{it.label}</div>
                <Icon size={28} weight="duotone" className="text-ink" />
              </div>
              <div
                className={`font-chunk text-6xl lg:text-7xl tracking-tight mt-5 mb-3 text-ink`}
                data-testid={`metric-${it.key}-value`}
              >
                {it.value.toLocaleString()}
              </div>
              <div className={`h-px w-full ${c.rule} my-3`} />
              <p className="text-sm text-ink leading-relaxed max-w-[36ch]">{it.blurb}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MetricsCards;
